import { useTranslation } from "react-i18next";
import { IPortinSlot } from "../IPortinSlot";
import { Controller } from "react-hook-form";
import type { FieldError, FieldName, FieldValues, UseFormMethods } from "react-hook-form";
import { max, min } from "date-fns";
import { TextFieldProps } from "@mui/material";
import { LabeledTextField } from "@common/LabeledTextField/LabeledTextField";
import { ResponsiveDatePicker } from "@common/ResponsiveDatePicker/ResponsiveDatePicker";
import { getObjectByPath } from "@utils/getObjectByPath";

interface IParams<TFieldValues extends FieldValues = FieldValues> {
    formMethods: UseFormMethods<TFieldValues>;
    fullWidth?: boolean;
    portinSlotList: IPortinSlot[];
    namePrefix?: string;
    defaultValue?: Date | null;
    clearCardError?(): void;
}

export const PortInDate = <TFieldValues extends FieldValues = FieldValues>(props: IParams<TFieldValues>) => {
    const { formMethods, portinSlotList, fullWidth, namePrefix, defaultValue, clearCardError } = props;
    const { control, clearErrors, errors } = formMethods;
    const { t } = useTranslation(["portin", "validation"]);
    const fieldName = namePrefix ? `${namePrefix}date` : "date";
    const fieldError = getObjectByPath<FieldError>(errors, fieldName);

    // Empty array passed to min or max will result in invalid date which cannot be formatted
    const portinSlotDateList = portinSlotList.length
        ? portinSlotList.map((portinSlot) => portinSlot.date)
        : [new Date()];
    const portinMinDate = min(portinSlotDateList);
    const portinMaxDate = max(portinSlotDateList);

    return (
        <Controller
            control={control}
            defaultValue={defaultValue ?? null}
            name={fieldName}
            render={(controllerRenderProps) => (
                <ResponsiveDatePicker
                    closeOnSelect
                    inputFormat="dd/MM/yyyy"
                    maxDate={portinMaxDate}
                    minDate={portinMinDate}
                    renderInput={(renderProps: TextFieldProps) => (
                        <LabeledTextField
                            {...renderProps}
                            className="xtest_date_input"
                            error={Boolean(fieldError)}
                            fullWidth={fullWidth}
                            helperText={fieldError?.message}
                            id="moveDate"
                            inputRef={controllerRenderProps.ref}
                            isRequired
                            labelText={t("portin:moveDate")}
                            name={fieldName}
                            placeholder="DD/MM/YYYY"
                            onFocus={(event) => {
                                renderProps.onFocus?.(event);
                                clearErrors(fieldName as FieldName<TFieldValues>);
                                clearCardError?.();
                            }}
                        />
                    )}
                    showToolbar={false}
                    value={controllerRenderProps.value ?? null}
                    onChange={(value) => {
                        clearErrors(fieldName as FieldName<TFieldValues>);
                        clearCardError?.();
                        controllerRenderProps.onChange(value);
                    }}
                />
            )}
            rules={{
                validate: (value) => Boolean(value) || t("validation:required"),
            }}
        />
    );
};
